import React, { useEffect, useMemo, useState } from 'react';
import { validateUploadMapping, UPLOAD_MAPPING_VERSION, type AssayAnalytes, type UploadMapping } from '@mariana/labos-client';
import { patchAssay, apiErrorMessage } from '../../api/limsClient';
import { gridFromFile, headersOf, previewRows, type Grid } from '../../utils/uploadMappingPreview';
import './AssayDefinition.css';

interface Props {
  assayId: number;
  definition: AssayAnalytes | null;
  mapping: UploadMapping | null;
  onSaved: () => Promise<void> | void;
}

interface ColumnDraft {
  column: string;
  /** '' = not mapped (the parser ignores the column). */
  key: string;
  unit: string;
}

const SKIP = '';

/**
 * For `capture: upload`: pick an example file, say which row holds the
 * headers and which column the sample id, map the other columns onto analyte
 * keys, see the first rows as the generic tabular parser would read them,
 * then save the result as `upload_mapping`.
 */
export const UploadMappingBuilder: React.FC<Props> = ({ assayId, definition, mapping, onSaved }) => {
  const [grid, setGrid] = useState<Grid | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [headerRow, setHeaderRow] = useState<number>(mapping?.headerRow ?? 1);
  const [sampleIdColumn, setSampleIdColumn] = useState<string>(mapping?.sampleIdColumn ?? '');
  const [columns, setColumns] = useState<ColumnDraft[]>(() => (mapping?.columns ?? []).map(c => ({ column: c.column, key: c.key, unit: c.unit ?? '' })));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const analyteKeys = useMemo(
    () => (definition?.analytes ?? []).filter(a => a.kind !== 'constant' && a.kind !== 'derived').map(a => a.key),
    [definition],
  );

  // Columns come from the file when there is one, else from the saved mapping.
  const headers = useMemo(() => {
    if (grid) return headersOf(grid, headerRow);
    const fromMapping = [mapping?.sampleIdColumn, ...(mapping?.columns ?? []).map(c => c.column)];
    return Array.from(new Set(fromMapping.filter((s): s is string => !!s)));
  }, [grid, headerRow, mapping]);

  // When the header row changes, keep the mappings whose column names survive and add the new ones unmapped.
  useEffect(() => {
    if (!grid) return;
    setColumns(prev => {
      const next = headers
        .filter(h => h !== sampleIdColumn)
        .map(h => prev.find(c => c.column === h) ?? { column: h, key: SKIP, unit: '' });
      const unchanged = next.length === prev.length && next.every((c, i) => c === prev[i]);
      return unchanged ? prev : next;
    });
  }, [grid, headers, sampleIdColumn]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setSaved(false);
    try {
      const g = await gridFromFile(file);
      if (g.length === 0) { setError('The file has no rows.'); return; }
      setGrid(g);
      setFileName(file.name);
      if (headerRow > g.length) setHeaderRow(1);
    } catch (err) {
      setError(`Could not read the file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const draft = (): UploadMapping => ({
    version: UPLOAD_MAPPING_VERSION,
    ...(headerRow !== 1 ? { headerRow } : {}),
    sampleIdColumn,
    columns: columns.filter(c => c.key !== SKIP).map(c => ({ column: c.column, key: c.key, ...(c.unit.trim() ? { unit: c.unit.trim() } : {}) })),
  });

  const checked = validateUploadMapping(draft());
  const validMapping = 'value' in checked ? (checked.value as UploadMapping) : null;
  const preview = grid && validMapping ? previewRows(grid, validMapping) : [];
  const previewKeys = validMapping ? validMapping.columns.map(c => c.key) : [];

  const save = async () => {
    if (!validMapping) { setError('error' in checked ? checked.error : 'mapping is incomplete'); return; }
    setSaving(true);
    setError(null);
    try {
      await patchAssay(assayId, { upload_mapping: validMapping });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const rowChoices = grid ? Array.from({ length: Math.min(grid.length, 20) }, (_, i) => i + 1) : [headerRow];

  return (
    <div className="assay-section">
      <h3 className="assay-section-heading">
        Upload mapping
        <span className="assay-muted">{mapping ? 'saved mapping loaded' : 'none saved yet'}</span>
      </h3>
      <div className="assay-form">
        <label htmlFor="mapping-file">Example file</label>
        <div>
          <input id="mapping-file" type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={e => onFile(e.target.files?.[0])} />
          {fileName && <span className="assay-muted"> {fileName}: {grid?.length ?? 0} rows</span>}
          {!grid && <div className="assay-note">Parsed in the browser; nothing is uploaded until you save the mapping.</div>}
        </div>
        <label htmlFor="mapping-header-row" title="The row of the file that holds the column names; the rows below it are samples">Header row</label>
        <select id="mapping-header-row" className="assay-select" style={{ width: '6rem' }} value={headerRow} onChange={e => setHeaderRow(Number(e.target.value))} disabled={!grid}>
          {rowChoices.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <label htmlFor="mapping-sample-id" title="The column whose value names the sample each row belongs to">Sample id column</label>
        <select id="mapping-sample-id" className="assay-select" style={{ maxWidth: '20rem' }} value={sampleIdColumn} onChange={e => setSampleIdColumn(e.target.value)}>
          <option value="">— choose —</option>
          {headers.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>

      {(grid || columns.length > 0) && (
        <table className="mapping-table">
          <thead>
            <tr><th>File column</th><th title="Which declared value the column holds; leave calculated columns unmapped, the LIMS computes derived values from their formulas">Analyte key</th><th>Unit (when the file has none)</th></tr>
          </thead>
          <tbody>
            {columns.map((c, i) => (
              <tr key={c.column}>
                <td><code>{c.column}</code></td>
                <td>
                  <select className="assay-select" value={c.key} aria-label={`Key for ${c.column}`} onChange={e => setColumns(columns.map((x, idx) => (idx === i ? { ...x, key: e.target.value } : x)))}>
                    <option value={SKIP}>— skip —</option>
                    {analyteKeys.map(k => <option key={k} value={k}>{k}</option>)}
                    {c.key && !analyteKeys.includes(c.key) && <option value={c.key}>{c.key} (not declared)</option>}
                  </select>
                </td>
                <td>
                  <input className="assay-input narrow" value={c.unit} aria-label={`Unit for ${c.column}`} disabled={c.key === SKIP} onChange={e => setColumns(columns.map((x, idx) => (idx === i ? { ...x, unit: e.target.value } : x)))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {analyteKeys.length === 0 && <div className="assay-note" style={{ marginTop: '0.375rem' }}>Declare the assay's analytes first; the file's columns map onto their keys.</div>}

      {grid && validMapping && (
        <div style={{ marginTop: '0.5rem' }}>
          <div className="assay-muted">Preview: first {preview.length} row{preview.length === 1 ? '' : 's'} as the parser would read them</div>
          <div className="preview-scroll">
            <table className="preview-table">
              <thead>
                <tr><th>Sample id</th>{previewKeys.map(k => <th key={k}>{k}</th>)}</tr>
              </thead>
              <tbody>
                {preview.length === 0 && <tr><td colSpan={previewKeys.length + 1} className="empty">No rows with a sample id under row {headerRow}.</td></tr>}
                {preview.map((r, i) => (
                  <tr key={i}>
                    <td>{r.sampleId}</td>
                    {previewKeys.map(k => {
                      const e = r.entries[k];
                      return <td key={k} className={e ? undefined : 'empty'}>{e ? `${e.value}${e.unit ? ` ${e.unit}` : ''}` : '—'}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="assay-actions">
        <button type="button" className="assay-btn primary" disabled={!validMapping || saving} onClick={save}
          title={!validMapping && 'error' in checked ? checked.error : undefined}>
          {saving ? 'Saving…' : 'Save upload mapping'}
        </button>
        {!validMapping && 'error' in checked && (sampleIdColumn || columns.some(c => c.key)) && <span className="assay-muted">{checked.error}</span>}
        {saved && <span className="assay-success">Saved</span>}
      </div>
      {error && <div className="assay-error" role="alert">{error}</div>}
    </div>
  );
};
